import $ from "cash-dom";
import { request, apiBase } from "@common/request";

/**
 * The Contact Us form, on /contact-us/ and at the foot of /aboutus/.
 *
 * THE MARKUP IS .in's, UNCHANGED — form#submitform950, the hidden
 * formid/accountid/userid, fields named "1" / "7" / "4" / "2533" / "2534", the
 * #failuremessage950 banner and the #submit950 anchor. That markup belongs to a
 * legacy marketing form which posted to `misc/subscribe` on .in's own origin.
 * None of it posts anywhere here: this module intercepts and sends JSON to the
 * gateway instead, so the DOM and the migrated SCSS still agree while the
 * request is International's.
 *
 * THE CONTRACT, verified against the stage gateway rather than assumed:
 *
 *   POST <API_URL>/contact
 *   { fullName, email, contactNumber, businessType, message }
 *
 *   201 { "success": true, "id": "<uuid>" }
 *   400 { "message": ["...", "..."], "error": "Bad Request", "statusCode": 400 }
 *
 * Four things that matter and are easy to get wrong:
 *
 *   1. `businessType` is an enum — advertiser | publisher | agency | brand |
 *      other. Nothing else is accepted, which is why the <select> is built from
 *      the read model's list and not from .in's (Business, Site Feedback, …).
 *   2. The property list is WHITELISTED. Sending `phone` or `subject` — names
 *      that look plausible — is a 400 "property phone should not exist", so
 *      this never posts a field the DTO does not name.
 *   3. AN EMPTY STRING IS NOT AN OMITTED FIELD. `contactNumber: ""` fails the
 *      gateway's phone check ("Enter a valid contact number") even though the
 *      field is optional when absent. Blank optionals are dropped, not sent
 *      empty — the same trap the submission payloads hit.
 *   4. The success test is `success === true`, not `if (data)`: a rejection is
 *      `{success: false, …}`, which is a truthy object.
 */

/**
 * Gateway field → .in's element id.
 *
 * getElementById, NOT a selector. `#1` and `#7` are not valid CSS — an
 * identifier cannot begin with a digit — so `$("#1")` throws in every engine.
 * The ids are .in's and stay .in's, so the lookup is what adapts.
 */
const IDS = {
  fullName: "1",
  email: "7",
  contactNumber: "4",
  businessType: "2533",
  message: "2534",
};

const FIELDS = Object.keys(IDS);

/** Client-side rules, mirroring the DTO so an obvious typo skips the round trip. */
const RULES = {
  fullName: (v) => (v ? "" : "Please enter your full name"),
  email: (v) => {
    if (!v) return "Please enter your email";
    // The same shape the React component tests; the gateway is the authority.
    return /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v) ? "" : "Enter a valid email";
  },
  contactNumber: (v) => {
    if (!v) return "Please enter your contact number";
    // Verified against stage: "1234567890", "847-780-1858" and
    // "+1 847-780-1858" all pass; "12345" and "abc" do not. The rule is about
    // how many digits there are, not how they are punctuated — the only rule
    // that can hold across eight countries' numbers. It also survives .in's
    // `type="number"`, which drops "+" and spaces before this ever sees them.
    return v.replace(/\D/g, "").length >= 7 ? "" : "Enter a valid contact number";
  },
  businessType: (v) => (v ? "" : "Please select a business type"),
  message: (v) => (v ? "" : "Please enter your message"),
};

export default function contactFormInit() {
  const form = $("#submitform950");
  if (!form.length) return;

  const banner = $("#failuremessage950");
  const trigger = $("#submit950");
  let sending = false;

  function node(name) {
    return document.getElementById(IDS[name]);
  }

  function value(name) {
    const element = node(name);
    return element ? String(element.value || "").trim() : "";
  }

  /**
   * .in ships ONE message element and styles it red inline. There is no success
   * node in this markup and adding one would change the DOM, so the same banner
   * carries both outcomes and only its colour moves.
   */
  function show(text, ok) {
    if (!banner.length) return;
    banner.text(text);
    banner.css("display", "block");
    banner.css("color", ok ? "#1a7f37" : "#ea2323");
    banner.css("border-color", ok ? "#d6e9c6" : "#ea2323");
  }

  function hide() {
    if (banner.length) banner.css("display", "none");
  }

  // Clearing as the user types: the banner may be holding a message about a
  // field they have already fixed.
  FIELDS.forEach(function (name) {
    const element = node(name);
    if (element) $(element).on("input change", hide);
  });

  function submit() {
    if (sending) return;
    hide();

    const values = {};
    let firstBad = "";
    let firstMessage = "";
    FIELDS.forEach(function (name) {
      values[name] = value(name);
      const error = RULES[name](values[name]);
      if (error && !firstBad) {
        firstBad = name;
        firstMessage = error;
      }
    });

    if (firstBad) {
      show(firstMessage, false);
      const element = node(firstBad);
      if (element && element.focus) element.focus();
      return;
    }

    // Built key by key rather than posted whole: see note 3 above — a blank
    // optional must be absent, and an unknown key is a 400.
    const payload = {};
    FIELDS.forEach(function (name) {
      if (values[name]) payload[name] = values[name];
    });

    sending = true;
    trigger.attr("data-state", "sending");
    const label = trigger.text();
    trigger.text("Submitting...");

    function done() {
      sending = false;
      trigger.removeAttr("data-state").text(label);
    }

    request
      .post(apiBase() + "/contact", payload, { throwErrors: true })
      .then(function (data) {
        if (data && data.success === true) {
          form[0].reset();
          show("Thank you! Your message has been sent successfully. We'll get back to you soon.", true);
          return;
        }
        show((data && data.message) || "Failed to submit. Please try again.", false);
      })
      .then(done, function (error) {
        // _fetch throws the Response itself when throwErrors is set, so the
        // gateway's own per-field messages are still readable — and worth
        // reading: they name the field, which a generic banner cannot.
        if (error && typeof error.json === "function") {
          return error
            .json()
            .then(function (body) {
              const messages = [].concat(body && body.message ? body.message : []);
              show(messages[0] || "Failed to submit. Please try again.", false);
            })
            .catch(function () {
              show("Failed to submit. Please try again.", false);
            })
            .then(done);
        }
        show("An error occurred. Please try again later.", false);
        done();
      });
  }

  // .in's Submit is an <a> whose inline onclick is inert on an anchor
  // (`this.type` is never "submit"), so the click has to be bound here.
  trigger.on("click", function (event) {
    event.preventDefault();
    submit();
  });

  // And the form's own submit, which the hidden <input type="submit"> still
  // fires when Enter is pressed in a text field.
  form.on("submit", function (event) {
    event.preventDefault();
    submit();
  });
}
